import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { 
  Users, 
  Search,
  Filter,
  ArrowRight,
  LogOut,
  Home,
  Mail,
  Phone,
  ShieldAlert
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/layout/AdminLayout';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLeads() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tab, setTab] = useState('active');
  const [spamCount, setSpamCount] = useState(0);
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      try {
        const url = tab === 'spam'
          ? `${API_URL}/api/admin/leads?status=spam`
          : `${API_URL}/api/admin/leads`;
        const response = await axios.get(url, { withCredentials: true });
        setLeads(response.data);
      } catch (error) {
        console.error('Error fetching leads:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, [tab]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/leads-spam-count`, { withCredentials: true })
      .then((r) => setSpamCount(r.data.count || 0)).catch(() => {});
  }, [tab]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-[#FFF7ED] text-[#F97316]',
      'contacted': 'bg-[#EFF6FF] text-[#1D4ED8]',
      'qualified': 'bg-green-100 text-green-700',
      'converted': 'bg-purple-100 text-purple-700',
      'closed': 'bg-[#F1F5F9] text-[#475569]'
    };
    return colors[status] || 'bg-[#F1F5F9] text-[#475569]';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminLayout title="Leads"><div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="heading-2 text-2xl text-[#0A0F1C]" data-testid="leads-title">
            {tab === 'spam' ? 'Spam' : 'Leads'} ({filteredLeads.length})
          </h1>
        </div>

        {/* Active / Spam tabs */}
        <div className="flex items-center gap-2 mb-6" data-testid="leads-tabs">
          <button
            onClick={() => { setTab('active'); setStatusFilter(''); }}
            data-testid="leads-tab-active"
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === 'active' ? 'bg-[#1D4ED8] text-white' : 'bg-white text-[#475569] border border-[#E2E8F0] hover:border-[#1D4ED8]'}`}
          >
            Active Leads
          </button>
          <button
            onClick={() => { setTab('spam'); setStatusFilter(''); }}
            data-testid="leads-tab-spam"
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors inline-flex items-center gap-2 ${tab === 'spam' ? 'bg-[#DC2626] text-white' : 'bg-white text-[#475569] border border-[#E2E8F0] hover:border-[#DC2626]'}`}
          >
            <ShieldAlert size={15} /> Spam
            {spamCount > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === 'spam' ? 'bg-white/25 text-white' : 'bg-red-100 text-red-700'}`}>{spamCount}</span>
            )}
          </button>
          <button
            onClick={async () => {
              setRescanning(true);
              try {
                const r = await axios.post(`${API_URL}/api/admin/leads/rescan-spam`, {}, { withCredentials: true });
                toast.success(r.data.flagged ? `Moved ${r.data.flagged} lead(s) to Spam` : 'No new spam found — you\u2019re all clear');
                setTab((t) => t); setSpamCount((c) => c + (r.data.flagged || 0));
                const url = tab === 'spam' ? `${API_URL}/api/admin/leads?status=spam` : `${API_URL}/api/admin/leads`;
                const res = await axios.get(url, { withCredentials: true });
                setLeads(res.data);
              } catch (e) { toast.error('Rescan failed'); }
              finally { setRescanning(false); }
            }}
            data-testid="leads-rescan-spam"
            disabled={rescanning}
            className="ml-auto px-4 py-2 rounded-lg text-sm font-bold bg-white text-[#475569] border border-[#E2E8F0] hover:border-[#DC2626] hover:text-[#DC2626] disabled:opacity-60 inline-flex items-center gap-2"
          >
            <ShieldAlert size={15} /> {rescanning ? 'Scanning…' : 'Rescan for spam'}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 border border-[#E2E8F0] rounded-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-grow relative">
              <Input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
                data-testid="leads-search"
              />
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input w-full md:w-48"
              data-testid="leads-status-filter"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          {filteredLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead className="bg-[#F9FAFB]">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Contact</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Source</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Interest</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {filteredLeads.map((lead, index) => (
                    <tr 
                      key={lead.lead_id || index} 
                      className="hover:bg-[#F9FAFB] cursor-pointer" 
                      data-testid={`lead-row-${index}`}
                      onClick={() => window.location.href = `/admin/leads/${lead.lead_id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#0A0F1C]">{lead.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-[#475569] flex items-center gap-1">
                            <Mail size={14} />
                            {lead.email}
                          </span>
                          {lead.phone && (
                            <span className="text-sm text-[#475569] flex items-center gap-1">
                              <Phone size={14} />
                              {lead.phone}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-[#475569]">{lead.source || '-'}</span>
                        {lead.is_spam && lead.spam_reason && (
                          <span className="block text-xs text-[#DC2626] font-medium mt-0.5">⚠ {lead.spam_reason.replace(/_/g, ' ')}</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-[#475569]">
                          {lead.service_interest || lead.resource_interest || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-[#475569]">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <Users size={48} className="text-[#E2E8F0] mx-auto mb-4" />
              <p className="text-[#475569]">No leads found</p>
            </div>
          )}
        </div>
      </div></AdminLayout>);}
